import { getLocalEventTime } from './tracking-local-time';

export const normalizeUnLocode = (value?: string | null) => {
  const normalized = (value || '').toUpperCase().replace(/[\s-]+/g, '');
  return /^[A-Z]{2}[A-Z0-9]{3}$/.test(normalized) ? normalized : null;
};

export const normalizeIsoCountryCode = (value?: string | null) => {
  const normalized = (value || '').trim().toUpperCase();
  return /^[A-Z]{2}$/.test(normalized) ? normalized : null;
};

/**
 * ISO-2 country of a tracked place, for flags and country names. Tracking
 * sends the country itself (airports have IATA codes like PVG with no
 * country); a UN/LOCODE (CNSHA) carries it in its first two letters; the
 * uploaded country is the last resort. Use this everywhere, not ad-hoc slices.
 */
export const trackingCountryCode = (place: {
  countryCode?: string | null;
  locode?: string | null;
  fallbackCountry?: string | null;
}) =>
  normalizeIsoCountryCode(place.countryCode) ||
  normalizeUnLocode(place.locode)?.slice(0, 2) ||
  normalizeIsoCountryCode(place.fallbackCountry);

export const countryCodeToFlag = (countryCode: string) =>
  String.fromCodePoint(
    ...countryCode
      .toUpperCase()
      .split('')
      .map((letter) => letter.charCodeAt(0) + 127397)
  );

const trackingEventLabels: Record<string, string> = {
  EMSH: 'Contenedor vacío entregado',
  GTIN: 'Ingreso a terminal',
  LOAD: 'Cargado en la nave',
  DEPA: 'Nave zarpó',
  ARRV: 'Arribo a puerto',
  DISC: 'Descargado de la nave',
  GTOT: 'Salida de terminal',
  EMPTY_RETURN: 'Contenedor vacío devuelto',
  STUF: 'Contenedor consolidado',
  STRP: 'Contenedor desconsolidado',
  // Air (IATA Cargo-IMP status codes)
  BKD: 'Reserva confirmada',
  FOH: 'Carga en bodega de origen',
  RCS: 'Recibida en aeropuerto de origen',
  MAN: 'Manifestada en vuelo',
  PRE: 'Preparada para el vuelo',
  DEP: 'Vuelo despegó',
  TFD: 'Transferida a otra aerolínea',
  RCT: 'Recibida de otra aerolínea',
  ARR: 'Vuelo aterrizó',
  RCF: 'Recibida del vuelo',
  NFD: 'Consignatario notificado',
  AWD: 'Documentos entregados',
  CCD: 'Liberada por aduana',
  DLV: 'Entregada al consignatario',
  DIS: 'Discrepancia reportada',
};

const trackingEventSequence: Record<string, number> = {
  EMSH: 20,
  STUF: 25,
  GTIN: 30,
  LOAD: 40,
  DEPA: 50,
  ARRV: 60,
  DISC: 70,
  GTOT: 80,
  STRP: 90,
  EMPTY_RETURN: 100,
  BKD: 20,
  FOH: 25,
  RCS: 30,
  MAN: 35,
  PRE: 38,
  DEP: 50,
  TFD: 52,
  RCT: 54,
  ARR: 60,
  RCF: 70,
  NFD: 75,
  AWD: 78,
  CCD: 80,
  DLV: 90,
};

export const getTrackingEventSequence = (eventType: string) =>
  trackingEventSequence[eventType.toUpperCase()] || 10;

export const getTrackingEventLabel = (eventType: string) =>
  trackingEventLabels[eventType.toUpperCase()] || eventType;

// Days between two events, counted on each event's local calendar.
export const getTrackingEventCalendarDayGap = (
  currentEventDate: string,
  nextEventDate: string,
  currentTimeZone?: string | null,
  nextTimeZone?: string | null
) => {
  const currentDay = getLocalEventTime(currentEventDate, currentTimeZone)?.day;
  const nextDay = getLocalEventTime(nextEventDate, nextTimeZone)?.day;
  if (!currentDay || !nextDay) return null;

  return Math.max(
    0,
    Math.round(
      (Date.parse(`${nextDay}T00:00:00Z`) -
        Date.parse(`${currentDay}T00:00:00Z`)) /
        86_400_000
    )
  );
};

// Elapsed time between two events with real times (air):
// "40 min", "4 h 44 min", "1 d 3 h".
export const getTrackingEventElapsedLabel = (
  currentEventDate: string,
  nextEventDate: string
) => {
  const minutes = Math.round(
    (Date.parse(nextEventDate) - Date.parse(currentEventDate)) / 60_000
  );
  if (!Number.isFinite(minutes)) return null;
  if (minutes <= 0) return '0 min';
  if (minutes < 60) return `${minutes} min`;

  const hours = Math.floor(minutes / 60);
  if (hours < 24) {
    const restMinutes = minutes % 60;
    return restMinutes ? `${hours} h ${restMinutes} min` : `${hours} h`;
  }
  const days = Math.floor(hours / 24);
  const restHours = hours % 24;
  return restHours ? `${days} d ${restHours} h` : `${days} d`;
};

/**
 * Air waybills end at the first confirmed delivery (DLV), as the airlines'
 * own trackers do. ShipsGo sometimes attaches later runs of the same flight
 * number (e.g. an estimated ARR at a stopover days after delivery).
 */
export const cutAirTimelineAtDelivery = <
  T extends {
    event_type: string;
    occurred_at: string;
    is_estimated?: boolean | null;
  },
>(
  events: T[],
  mode?: string | null
): T[] => {
  if (mode !== 'air') return events;
  const deliveredAt = events
    .filter(
      (event) => !event.is_estimated && event.event_type.toUpperCase() === 'DLV'
    )
    .map((event) => new Date(event.occurred_at).getTime())
    .filter(Number.isFinite)
    .sort((a, b) => a - b)[0];
  if (deliveredAt === undefined) return events;
  return events.filter(
    (event) => new Date(event.occurred_at).getTime() <= deliveredAt
  );
};
